import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  TextField,
  Container,
  Typography,
  Box,
  Button,
  Grid,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

const AddBlog = () => {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedImages, setSelectedImages] = useState([]); // Store multiple selected files
  const [imagePreviews, setImagePreviews] = useState([]); // For previews
  const [author, setAuthor] = useState("");
  const [date, setDate] = useState(""); // Optional: YYYY-MM-DD format
  const [blogs, setBlogs] = useState([]); // For validation (fetch existing)
  const baseUrl = process.env.REACT_APP_BASE_URL;

  // Fetch existing blogs for validation (similar to AddRole)
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await axios.get(`${baseUrl}/auth/blogs`);
        setBlogs(response.data);
      } catch (error) {
        console.error("Error fetching blogs for validation:", error);
      }
    };
    fetchBlogs();
  }, [baseUrl]);

  // Handle multiple image selection and preview
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Validate file types and sizes
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
    const maxSize = 5 * 1024 * 1024; // 5MB per file

    const validFiles = [];
    const newPreviews = [];

    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        alert(
          `File ${file.name} is not allowed. Only JPG, JPEG, PNG, and GIF files are allowed.`,
        );
        continue;
      }
      if (file.size > maxSize) {
        alert(`File ${file.name} is too large. Maximum size is 5MB.`);
        continue;
      }
      validFiles.push(file);
      newPreviews.push(URL.createObjectURL(file));
    }

    if (validFiles.length > 0) {
      setSelectedImages([...selectedImages, ...validFiles]);
      setImagePreviews([...imagePreviews, ...newPreviews]);
    }
  };

  // Remove an image from the selection
  const handleRemoveImage = (index) => {
    const newImages = selectedImages.filter((_, i) => i !== index);
    const newPreviews = imagePreviews.filter((_, i) => i !== index);

    // Revoke the URL to free memory
    URL.revokeObjectURL(imagePreviews[index]);

    setSelectedImages(newImages);
    setImagePreviews(newPreviews);
  };

  // Clear previews when component unmounts
  useEffect(() => {
    return () => {
      imagePreviews.forEach((preview) => URL.revokeObjectURL(preview));
    };
  }, []);

  const validateForm = () => {
    if (
      !title.trim() ||
      !description.trim() ||
      selectedImages.length === 0 ||
      !author.trim()
    ) {
      alert("Title, description, at least one image, and author are required.");
      return false;
    }
    const existingBlog = blogs.find(
      (blog) => blog.title.toLowerCase() === title.toLowerCase(),
    );
    if (existingBlog) {
      alert(
        "Blog with this title already exists. Please choose a different title.",
      );
      return false;
    }
    return true;
  };

  const handleAddBlog = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      const formData = new FormData();
      formData.append("title", title);
      formData.append("description", description);

      // Append all selected images
      selectedImages.forEach((image) => {
        formData.append("images", image); // Field name matches Multer array configuration
      });

      formData.append("author", author);
      formData.append("date", date || new Date().toISOString().split("T")[0]); // Default to today if empty

      try {
        await axios.post(`${baseUrl}/auth/create/blog`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        alert("Blog added successfully");
        navigate("/dashboard/blogs"); // Redirect to blog list
      } catch (error) {
        console.error("Error adding blog:", error);
        alert(
          "Blog addition failed: " +
            (error.response?.data?.error || "Unknown error"),
        );
      }
    }
  };

  return (
    <Container maxWidth="xl">
      <Typography variant="h4" component="h1" sx={{ mt: 2 }} gutterBottom>
        Add Blog Post
      </Typography>
      <form onSubmit={handleAddBlog}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              margin="normal"
              label="Title"
              variant="outlined"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              margin="normal"
              label="Description"
              variant="outlined"
              multiline
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageChange}
              style={{ margin: "16px 0", display: "block" }}
            />
            {imagePreviews.length > 0 && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                  gap: 2,
                  mt: 2,
                }}
              >
                {imagePreviews.map((preview, index) => (
                  <Box
                    key={index}
                    sx={{
                      position: "relative",
                      borderRadius: "8px",
                      overflow: "hidden",
                    }}
                  >
                    <img
                      src={preview}
                      alt={`Preview ${index + 1}`}
                      style={{
                        width: "100%",
                        height: "150px",
                        objectFit: "cover",
                        borderRadius: "8px",
                      }}
                    />
                    <Button
                      variant="contained"
                      color="error"
                      size="small"
                      onClick={() => handleRemoveImage(index)}
                      sx={{
                        position: "absolute",
                        top: 5,
                        right: 5,
                        minWidth: "auto",
                        padding: "4px 8px",
                      }}
                    >
                      Remove
                    </Button>
                  </Box>
                ))}
              </Box>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              margin="normal"
              label="Author"
              variant="outlined"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              margin="normal"
              label="Publication Date (Optional)"
              variant="outlined"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, mb: 16 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            sx={{ width: "25%" }} // Reduced width as per previous request
          >
            Add Blog
          </Button>
        </Box>
      </form>
    </Container>
  );
};

export default AddBlog;
